import { useEffect, useState } from 'react';
import PageHeader from '../../../components/owner/PageHeader';
import { defaultSettings, currencyOptions } from './settingsData';
import { usePlatformSettings } from '../../../context/PlatformSettingsContext';
import { usePanelToast } from '../../../context/PanelToastContext';
import '../ownerPages.css';
import '../subscriptions/subscriptions.css';
import './settings.css';

function Settings() {
  const toast = usePanelToast();
  const { settings: savedSettings, saveSettings, loading: settingsLoading } = usePlatformSettings();
  const [settings, setSettings] = useState(savedSettings || defaultSettings);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (savedSettings) setSettings(savedSettings);
  }, [savedSettings]);

  const handleSoftwareNameChange = (e) => {
    setSettings((prev) => ({ ...prev, softwareName: e.target.value }));
  };

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    if (name === 'code') {
      const selected = currencyOptions.find((c) => c.code === value);
      setSettings((prev) => ({
        ...prev,
        currency: {
          ...prev.currency,
          code: value,
          symbol: selected?.symbol || prev.currency.symbol,
        },
      }));
    } else {
      setSettings((prev) => ({
        ...prev,
        currency: { ...prev.currency, [name]: name === 'decimalPlaces' ? Number(value) : value },
      }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await saveSettings(settings);
      toast.showSaved('Settings');
    } catch (err) {
      setError(err.message || 'Failed to save settings');
      toast.showError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const formatPreview = (amount = 12500) => {
    const formatted = amount.toLocaleString(undefined, {
      minimumFractionDigits: settings.currency.decimalPlaces,
      maximumFractionDigits: settings.currency.decimalPlaces,
    });
    return settings.currency.position === 'before'
      ? `${settings.currency.symbol} ${formatted}`
      : `${formatted} ${settings.currency.symbol}`;
  };

  return (
    <div className="owner-settings-page">
      <PageHeader title="Settings" subtitle="Software name and currency for the owner panel and gym admin registration" />
      {settingsLoading && <p className="owner-placeholder-text owner-mb-16">Loading settings...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="owner-card rec-panel-card owner-panel-section settings-section">
        <h2 className="owner-card-title">Software Name</h2>
        <div className="settings-form-grid">
          <div className="owner-form-group settings-form-full">
            <label className="owner-form-label" htmlFor="software-name">Software Name</label>
            <input
              id="software-name"
              className="owner-form-input"
              value={settings.softwareName}
              onChange={handleSoftwareNameChange}
              placeholder="Enter software name"
            />
          </div>
        </div>
      </div>

      <div className="owner-card rec-panel-card owner-panel-section settings-section">
        <h2 className="owner-card-title">Currency</h2>
        <div className="settings-form-grid">
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="currency-code">Currency</label>
            <select
              id="currency-code"
              name="code"
              className="owner-form-select"
              value={settings.currency.code}
              onChange={handleCurrencyChange}
            >
              {currencyOptions.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="currency-symbol">Currency Symbol</label>
            <input
              id="currency-symbol"
              name="symbol"
              className="owner-form-input"
              value={settings.currency.symbol}
              onChange={handleCurrencyChange}
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="currency-position">Symbol Position</label>
            <select
              id="currency-position"
              name="position"
              className="owner-form-select"
              value={settings.currency.position}
              onChange={handleCurrencyChange}
            >
              <option value="before">Before amount (Rs 1,000)</option>
              <option value="after">After amount (1,000 Rs)</option>
            </select>
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="currency-decimals">Decimal Places</label>
            <select
              id="currency-decimals"
              name="decimalPlaces"
              className="owner-form-select"
              value={settings.currency.decimalPlaces}
              onChange={handleCurrencyChange}
            >
              <option value={0}>0</option>
              <option value={2}>2</option>
            </select>
          </div>
          <div className="owner-form-group settings-form-full">
            <div className="settings-currency-preview">
              <div className="settings-currency-preview-label">Preview</div>
              <div className="settings-currency-preview-value">{formatPreview()}</div>
            </div>
          </div>
        </div>

        <div className="settings-save-bar">
          <button type="button" className="owner-btn owner-btn-primary" onClick={handleSave} disabled={saving || settingsLoading}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Settings;
